import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField, Card } from '@totalsoft/rocket-ui';
import Income from '@mui/icons-material/PlaylistAddCheck';
import { Prompt } from 'react-router-dom';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import FormulaField from 'features/common/components/formula-field';
import { FormulaFieldContainer, FormulaFieldLabel } from 'components/common/Styles';

const ScoringCategoryGeneralDataComponent = ({
  scoringCategory,
  dispatch,
  inputTypes,
  scales,
  visibleNumeric,
  defaultDetail,
  defaultLookup,
  validation
}) => {
  const { t } = useTranslation();

  const onHeaderPropertyChanged = (propertyName, propertyValue) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, propertyValue });
  };

  const onLookupPropertyChanged = event => {
    const scoringDetailId = event !== null ? event.scoringDetailId : null;
    const scoringDetailName = event !== null ? event.scoringDetailName : '';
    dispatch({ type: 'OnLookupPropertyChanged', propertyId: 'scoringDetailId', scoringDetailId, scoringDetailName });
  };

  return (
    <Card icon={Income} iconColor='primary' title={t('ScoringCategories.GeneralData.Title')}>
      <Prompt when={scoringCategory.changed} message={t('ScoringCategories.LeavingWithoutSavingScoringCategory')} />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={2}>
          <TextField
            isNumeric
            fullWidth
            value={scoringCategory.orderNo}
            decimalScale={0}
            onChange={event => onHeaderPropertyChanged('orderNo', event)}
            label={t('ScoringCategories.Header.OrderNo')}
            error={!isValid(validation.orderNo)}
            helperText={getErrors(validation.orderNo)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            value={scoringCategory.scoringCode || ''}
            onChange={event => onHeaderPropertyChanged('scoringCode', event)}
            label={t('ScoringCategories.Header.Code')}
            error={!isValid(validation.scoringCode)}
            helperText={getErrors(validation.scoringCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            value={scoringCategory.scoringName || ''}
            onChange={event => onHeaderPropertyChanged('scoringName', event)}
            label={t('ScoringCategories.Header.Name')}
            error={!isValid(validation.scoringName)}
            helperText={getErrors(validation.scoringName)}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Autocomplete
            fullWidth
            value={scoringCategory.inputTypeId}
            simpleValue
            options={inputTypes}
            onChange={event => onHeaderPropertyChanged('inputTypeId', event)}
            label={t('ScoringCategories.Header.InputTypeId')}
          />
        </Grid>
        {visibleNumeric === 2 && (
          <Grid item xs={12} sm={2}>
            <Autocomplete
              fullWidth
              labelKey='id'
              valueKey='value'
              value={scoringCategory.scale}
              simpleValue
              options={scales}
              onChange={event => {
                onHeaderPropertyChanged('scale', event);
              }}
              label={t('ScoringCategories.Header.Scale')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} lg={8}>
          <FormulaFieldLabel>{t('ScoringCategories.Header.Formula')}</FormulaFieldLabel>
          <FormulaFieldContainer>
            <FormulaField value={scoringCategory.formula} onChange={value => onHeaderPropertyChanged('formula', value)} />
          </FormulaFieldContainer>
        </Grid>
        <Grid item xs={12} sm={2} mt={2}>
          <FormControlLabel
            control={<Checkbox checked={scoringCategory.computed || false} />}
            label={t('ScoringCategories.Header.Computed')}
            onChange={event => onHeaderPropertyChanged('computed', event.target.checked)}
          />
        </Grid>
        <Grid item xs={12} sm={2} mt={2}>
          <FormControlLabel
            control={<Checkbox checked={scoringCategory.sensitiveInfo || false} />}
            label={t('ScoringCategories.Header.SensitiveInfo')}
            onChange={event => onHeaderPropertyChanged('sensitiveInfo', event.target.checked)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            fullWidth
            isClearable={true}
            labelKey='scoringDetailName'
            valueKey='scoringDetailId'
            value={{
              scoringDetailId: defaultDetail.scoringDetailId,
              scoringDetailName: defaultDetail.scoringDetailName
            }}
            options={defaultLookup}
            onChange={event => onLookupPropertyChanged(event)}
            label={t('ScoringCategories.Header.Default')}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

ScoringCategoryGeneralDataComponent.propTypes = {
  scoringCategory: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  inputTypes: PropTypes.array.isRequired,
  scales: PropTypes.array.isRequired,
  defaultDetail: PropTypes.object.isRequired,
  defaultLookup: PropTypes.array,
  validation: PropTypes.object.isRequired,
  visibleNumeric: PropTypes.number
};

export default ScoringCategoryGeneralDataComponent;
